import { useTitle } from '@/hooks/useTitle';
import styles from './styles.module.scss';

export function TermsPage() {
  useTitle('DienTEA | Términos y Condiciones');

  return (
    <main className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>Términos y Condiciones</h1>
          <p className={styles.subtitle}>Última actualización: mayo de 2026</p>
        </header>

        <section className={styles.section}>
          <div className={styles.notice}>
            <strong>Uso del sitio:</strong> El acceso y uso de este sitio web implica la aceptación plena de los
            presentes Términos y Condiciones. Si no está de acuerdo con alguno de ellos, le rogamos que se abstenga de
            utilizar el servicio.
          </div>
        </section>

        <section className={styles.section}>
          <h2>1. Objeto del servicio</h2>
          <p>
            Este sitio web ofrece a familias un espacio con vídeos seleccionados y recursos visuales (pictogramas y
            apoyos visuales) relacionados con la primera visita al dentista, con especial atención a niños con Trastorno
            del Espectro Autista (TEA).
          </p>
          <p>
            Todo el contenido ha sido revisado por profesionales de la odontopediatría con el objetivo de garantizar que
            se basa en evidencia científica y experiencia clínica. No obstante, este sitio{' '}
            <strong>no constituye un servicio médico o clínico</strong>, y sus contenidos no reemplazan la consulta con
            un especialista.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Acceso y uso del sitio</h2>
          <p>
            El acceso al sitio es gratuito y no requiere registro. El usuario se compromete a hacer un uso lícito del
            mismo y a no llevar a cabo ninguna de las siguientes acciones:
          </p>
          <ul className={styles.list}>
            <li>Utilizar el sitio de forma contraria a la ley, la moral o el orden público.</li>
            <li>Reproducir, distribuir o explotar comercialmente los contenidos sin autorización escrita previa.</li>
            <li>Introducir o difundir virus, malware o cualquier otro elemento dañino.</li>
            <li>Realizar cualquier acción que pueda dañar, inutilizar o sobrecargar el sitio.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>3. Contenido propio: vídeos seleccionados y recursos visuales</h2>
          <p>Los vídeos disponibles en el sitio han sido seleccionados y clasificados según:</p>
          <ul className={styles.list}>
            <li>Tipo de vídeo y finalidad educativa.</li>
            <li>Idioma del contenido.</li>
            <li>Autor o creador del material.</li>
            <li>Coherencia con la evidencia científica disponible.</li>
          </ul>
          <p>
            Se han excluido expresamente aquellos vídeos con información falsa o no respaldada por evidencia científica.
            No obstante, dado que los contenidos de YouTube pueden modificarse o eliminarse por sus creadores en
            cualquier momento, las titulares no garantizan la disponibilidad permanente de todos los vídeos.
          </p>
          <p>
            Los pictogramas y apoyos visuales disponibles en el sitio son de elaboración propia o han sido obtenidos
            bajo licencia. Su uso está permitido exclusivamente para fines personales, educativos y no comerciales.
          </p>
        </section>

        <section className={styles.section}>
          <h2>4. Contenido de terceros: YouTube</h2>
          <p>
            Los vídeos incrustados en el sitio están alojados en <strong>YouTube</strong>
            (Google LLC) y sujetos a las{' '}
            <a href='https://www.youtube.com/t/terms' target='_blank' rel='noopener noreferrer'>
              Condiciones del Servicio de YouTube
            </a>
            . Las titulares de este sitio no son responsables de los cambios que YouTube realice sobre la
            disponibilidad, contenido o funcionalidad de dichos vídeos.
          </p>
          <p>
            Al reproducir un vídeo, el usuario acepta las condiciones de uso de YouTube y la posibilidad de que dicho
            servicio establezca cookies en su navegador, tal como se describe en la{' '}
            <a href='/politica-de-cookies'>Política de Cookies</a>.
          </p>
        </section>

        <section className={styles.section}>
          <h2>5. Propiedad intelectual</h2>
          <p>
            Los contenidos propios del sitio web —textos, pictogramas, diseño, estructura y código— son propiedad de sus
            titulares y están protegidos por la legislación española e internacional de propiedad intelectual.
          </p>
          <p>
            Se permite la descarga o impresión de materiales para uso personal y no comercial, siempre que se mantenga
            la atribución a las autoras. Cualquier otro uso requiere autorización escrita previa.
          </p>
        </section>

        <section className={styles.section}>
          <h2>6. Exclusión de responsabilidad</h2>
          <p>
            Las titulares del sitio no garantizan la disponibilidad y continuidad ininterrumpida del servicio, ni la
            ausencia de errores en los contenidos. En ningún caso serán responsables de los daños y perjuicios que
            puedan derivarse de:
          </p>
          <ul className={styles.list}>
            <li>La interrupción, suspensión o desconexión del sitio web.</li>
            <li>La presencia de virus u otros elementos dañinos en los contenidos.</li>
            <li>El uso inadecuado del sitio por parte del usuario.</li>
            <li>La indisponibilidad o cambio de vídeos alojados en YouTube.</li>
            <li>
              Decisiones tomadas por el usuario basándose en los contenidos del sitio sin consultar previamente a un
              profesional de la salud.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>7. Modificaciones</h2>
          <p>
            Las titulares se reservan el derecho a modificar en cualquier momento el contenido del sitio web, así como
            los presentes Términos y Condiciones, la Política de Privacidad y la Política de Cookies. Los cambios
            entrarán en vigor desde el momento de su publicación en el sitio. El uso continuado del sitio tras dichos
            cambios implicará su aceptación.
          </p>
        </section>

        <section className={styles.section}>
          <h2>8. Legislación aplicable y resolución de conflictos</h2>
          <p>
            Los presentes Términos y Condiciones se rigen por la legislación española. Cualquier controversia que
            pudiera surgir en relación con el acceso o uso del sitio web se someterá a los Juzgados y Tribunales del
            domicilio del titular.
          </p>
          <p>
            Asimismo, conforme al Reglamento (UE) 524/2013, le informamos de la existencia de la{' '}
            <a href='https://ec.europa.eu/consumers/odr' target='_blank' rel='noopener noreferrer'>
              Plataforma de Resolución de Litigios en línea de la Comisión Europea
            </a>
            .
          </p>
        </section>

        <section className={styles.section}>
          <h2>9. Contacto</h2>
          <p>
            Para cualquier consulta sobre estos Términos y Condiciones, puede contactarnos en{' '}
            <a href='mailto:contacto@dientea.es'>contacto@dientea.es</a>.
          </p>
        </section>
      </div>
    </main>
  );
}
